import { useState } from "react";
import { Building2, Handshake, Calculator, Clock4, Lightbulb, AlertTriangle, Sparkles, Lock, FileText, RefreshCw } from "lucide-react";
import { renderMdInline, Markdownish } from "./markdown";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { getProToken } from "../../lib/pro";
import { toast } from "sonner";

const TONE_OPTIONS = [
  { id: "direct", label: "Direct", hint: "Blunt, no hedging" },
  { id: "gentle", label: "Gentle", hint: "Warm and encouraging" },
  { id: "analyst", label: "Analyst", hint: "Data-first, dry register" },
];

/**
 * "In Plain English" closing section.
 *
 * On FREE: shows only the 1-sentence verdict + a Pro tease.
 * On PRO:  shows the full narrative + all 4 original quadrants + 4 new
 *          "what this means for X" breakouts + runway-in-plain-english +
 *          bottom line.
 *
 * No dark backgrounds. The whole report is light/cream, and the conclusion
 * matches — that's how CFOs actually deliver memos.
 */
export default function PlainEnglishConclusion({ summary, proActive, onUnlock, report }) {
  if (!summary) return null;

  // ----- FREE flow: verdict-only tease ---------------------------------
  if (!proActive) {
    return (
      <div
        className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-9 mb-6"
        data-testid="plain-english-conclusion"
      >
        {summary.verdict && (
          <>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C26D5C] font-medium">
              The bottom line
            </p>
            <h3
              className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] leading-snug tracking-tight mt-2 max-w-3xl"
              data-testid="plain-english-verdict"
            >
              {renderMdInline(summary.verdict)}
            </h3>
          </>
        )}

        <div className="mt-7 pt-6 border-t border-[#E5E0D8] flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C26D5C]">
              <Lock size={11} strokeWidth={1.5} />
              Pro feature
            </div>
            <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] tracking-tight leading-snug mt-2">
              Get the full plain-English read on your business.
            </p>
            <p className="text-sm text-[#5C665D] leading-relaxed mt-2">
              The 600-word narrative from a CFO-as-a-friend perspective, plus what this means for
              borrowing money, selling, taxes, and your runway — all without the jargon.
            </p>
          </div>
          <Button
            onClick={onUnlock}
            className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-5 transition-all duration-300 whitespace-nowrap shrink-0"
            data-testid="plain-english-unlock-btn"
          >
            <Sparkles size={14} strokeWidth={1.5} className="mr-2" />
            Unlock with Pro
          </Button>
        </div>
      </div>
    );
  }

  // ----- PRO flow: full narrative + breakouts (all light backgrounds) ----
  return (
    <div className="mb-6" data-testid="plain-english-conclusion">
      {/* Verdict card — light cream */}
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-9">
        {summary.verdict && (
          <>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C26D5C] font-medium">
              The bottom line
            </p>
            <h3
              className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#1F382E] leading-snug tracking-tight mt-2 max-w-4xl"
              data-testid="plain-english-verdict"
            >
              {renderMdInline(summary.verdict)}
            </h3>
          </>
        )}
      </div>

      {/* Narrative — substantial paid surface, formatted like a memo */}
      {summary.narrative && (
        <NarrativeMemo
          initialNarrative={summary.narrative}
          summary={summary}
          report={report}
        />
      )}

      {/* Structured plain-English breakouts */}
      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <Breakout
          icon={Lightbulb}
          label="How you're doing"
          text={summary.how_we_re_doing}
          testid="plain-english-how-we-re-doing"
        />
        <Breakout
          icon={Lightbulb}
          label="What's working"
          text={summary.what_s_working}
          testid="plain-english-what-s-working"
          tone="success"
        />
        <Breakout
          icon={AlertTriangle}
          label="What's worrying"
          text={summary.what_s_worrying}
          testid="plain-english-what-s-worrying"
          tone="warning"
        />
        <Breakout
          icon={Lightbulb}
          label="What to do next"
          text={summary.what_to_do_next}
          testid="plain-english-what-to-do-next"
          tone="accent"
        />

        {/* "What this means for…" breakouts */}
        <Breakout
          icon={Building2}
          label="What this means for borrowing money"
          text={summary.what_for_borrowing}
          testid="plain-english-borrowing"
        />
        <Breakout
          icon={Handshake}
          label="What this means if you wanted to sell"
          text={summary.what_for_selling}
          testid="plain-english-selling"
        />
        <Breakout
          icon={Calculator}
          label="What this means for your taxes"
          text={summary.what_for_taxes}
          testid="plain-english-taxes"
        />
        <Breakout
          icon={Clock4}
          label="Your runway in plain English"
          text={summary.runway_in_plain_english}
          testid="plain-english-runway"
        />
      </div>

      {/* Final takeaway */}
      {summary.bottom_line && (
        <div className="mt-4 bg-[#F7F5F0] border border-[#C26D5C]/30 rounded-2xl p-6 sm:p-8 max-w-4xl">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
            The takeaway
          </p>
          <p
            className="font-serif-display text-xl sm:text-2xl text-[#1F382E] leading-snug italic mt-2"
            data-testid="plain-english-bottom-line"
          >
            {renderMdInline(summary.bottom_line)}
          </p>
        </div>
      )}
    </div>
  );
}

function Breakout({ icon: Icon, label, text, testid, tone }) {
  if (!text) return null;
  const accent =
    tone === "success" ? "text-[#2C5C43]"
    : tone === "warning" ? "text-[#A84632]"
    : tone === "accent" ? "text-[#C26D5C]"
    : "text-[#5C665D]";
  return (
    <div
      className="bg-white border border-[#E5E0D8] rounded-2xl p-5 sm:p-6"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} strokeWidth={1.5} className={accent} />
        <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-[#5C665D]">
          {label}
        </p>
      </div>
      <p className="text-[14px] text-[#1A1A1A] leading-relaxed">
        {renderMdInline(text)}
      </p>
    </div>
  );
}

/**
 * Renders the narrative card with a "Re-roll tone" control underneath.
 * Tone changes never mutate the persisted report — they're purely local,
 * so users can experiment freely.
 *
 * The rewrite button is print-hidden so the exported PDF always shows the
 * narrative as-rendered without controls bleeding through.
 */
function NarrativeMemo({ initialNarrative, summary, report }) {
  const [narrative, setNarrative] = useState(initialNarrative);
  const [activeTone, setActiveTone] = useState("original");
  const [loadingTone, setLoadingTone] = useState(null);

  async function handleRewrite(toneId) {
    const token = getProToken();
    if (!token) {
      toast.error("Pro pass required to re-roll the tone.");
      return;
    }
    if (loadingTone) return;
    setLoadingTone(toneId);
    try {
      const payload = {
        unlock_token: token,
        tone: toneId,
        summary,
        metrics: report?.metrics || {},
        company: report?.input?.company_name,
        currency: report?.input?.currency,
        industry: report?.input?.industry,
      };
      const { data } = await api.post("/report/rewrite-narrative", payload);
      if (data?.rewritten && data.narrative) {
        setNarrative(data.narrative);
        setActiveTone(toneId);
        toast.success(`Rewritten in ${data.tone_label || toneId} voice.`);
      } else {
        const reason = data?.reason || "unknown";
        if (reason === "timeout") {
          toast.error("Rewrite timed out. Try again in a moment.");
        } else if (reason === "ai_unavailable") {
          toast.error("AI is unavailable right now. Try again later.");
        } else {
          toast.error("Couldn't rewrite that one — keeping the original.");
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Your Pro pass isn't active. Restore or upgrade to re-roll.");
      } else {
        toast.error("Rewrite failed. Please try again.");
      }
    } finally {
      setLoadingTone(null);
    }
  }

  function handleReset() {
    setNarrative(initialNarrative);
    setActiveTone("original");
  }

  return (
    <div
      className="mt-4 bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-10"
      data-testid="plain-english-narrative"
    >
      <div className="flex items-center gap-2.5 mb-1">
        <FileText size={14} strokeWidth={1.5} className="text-[#C26D5C]" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
          The CFO memo, in plain English
        </p>
      </div>
      <p className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] tracking-tight leading-tight mb-5 max-w-3xl">
        Your business, explained like a friend who's also a CFO.
      </p>
      <div
        className="max-w-3xl [&_p]:text-[15px] [&_p]:leading-[1.75] [&_strong]:text-[#1F382E] [&_strong]:font-medium"
        data-testid="plain-english-narrative-body"
      >
        <Markdownish text={narrative} />
      </div>

      {/* Tone controls — never printed */}
      <div
        className="mt-6 pt-5 border-t border-[#E5E0D8] flex flex-col sm:flex-row sm:items-center gap-3 print:hidden"
        data-testid="narrative-rewrite-controls"
      >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#5C665D] font-medium">
          <RefreshCw size={11} strokeWidth={1.5} className="text-[#C26D5C]" />
          Re-roll tone
        </div>
        <div className="flex flex-wrap gap-2">
          {TONE_OPTIONS.map((t) => {
            const isActive = activeTone === t.id;
            const isLoading = loadingTone === t.id;
            return (
              <button
                key={t.id}
                type="button"
                disabled={!!loadingTone}
                onClick={() => handleRewrite(t.id)}
                title={t.hint}
                className={
                  "text-[12px] tracking-tight px-3 py-1.5 rounded-full border transition-all duration-200 " +
                  (isActive
                    ? "bg-[#1F382E] text-[#F7F5F0] border-[#1F382E]"
                    : "bg-white text-[#1F382E] border-[#E5E0D8] hover:border-[#C26D5C] hover:text-[#C26D5C]") +
                  (loadingTone && !isLoading ? " opacity-50 cursor-not-allowed" : "")
                }
                data-testid={`narrative-tone-${t.id}`}
              >
                {isLoading ? "Rewriting…" : t.label}
              </button>
            );
          })}
          {activeTone !== "original" && (
            <button
              type="button"
              disabled={!!loadingTone}
              onClick={handleReset}
              className="text-[12px] tracking-tight px-3 py-1.5 rounded-full border bg-white text-[#5C665D] border-[#E5E0D8] hover:text-[#1F382E] hover:border-[#1F382E] transition-all duration-200"
              data-testid="narrative-tone-reset"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
